import { useState } from 'react'
import { FiX, FiClock, FiCalendar, FiAlertCircle, FiSend } from 'react-icons/fi'
import toast from 'react-hot-toast'
import { API_URL } from '../config/api'
import { formatearFecha } from '../utils/fechas'

const PropuestaCorreccionModal = ({ fichaje, onClose, onSuccess, buildApiUrl }) => {
  const [motivo, setMotivo] = useState('')
  const [nuevaHoraEntrada, setNuevaHoraEntrada] = useState(fichaje?.hora_entrada?.substring(0, 5) || '')
  const [nuevaHoraSalida, setNuevaHoraSalida] = useState(fichaje?.hora_salida?.substring(0, 5) || '')
  const [loading, setLoading] = useState(false)

  const apiUrl = buildApiUrl ? buildApiUrl('/correcciones/proponer') : `${API_URL}/correcciones/proponer`

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!motivo.trim()) {
      toast.error('Debes indicar el motivo de la propuesta de corrección')
      return
    }

    if (!nuevaHoraEntrada) {
      toast.error('Debes indicar la hora de entrada corregida')
      return
    }

    try {
      setLoading(true)
      const token = localStorage.getItem('token')

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fichaje_id: fichaje.id,
          empleado_id: fichaje.empleado_id,
          hora_entrada_corregida: nuevaHoraEntrada,
          hora_salida_corregida: nuevaHoraSalida || null,
          motivo
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Error al enviar propuesta')
      }

      toast.success('Propuesta de corrección enviada al empleado')
      onSuccess()
    } catch (error) {
      console.error('Error:', error)
      toast.error(error.message || 'Error al enviar propuesta')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="card-glass max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200/50">
          <h3 className="text-lg font-semibold text-gray-900">
            Proponer Corrección
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Fichaje Info */}
        <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200/50">
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
            <FiCalendar size={16} />
            <span className="font-medium">{fichaje.empleado_nombre || 'Empleado'}</span>
            <span>-</span>
            <span>{formatearFecha(fichaje.fecha)}</span>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <FiClock size={16} className="text-green-600" />
              <span>Entrada: <strong>{fichaje.hora_entrada || '-'}</strong></span>
            </div>
            {fichaje.hora_salida && (
              <div className="flex items-center gap-2">
                <FiClock size={16} className="text-red-600" />
                <span>Salida: <strong>{fichaje.hora_salida}</strong></span>
              </div>
            )}
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Motivo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de la propuesta de corrección *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Explica al empleado por qué propones esta corrección..."
              required
            />
          </div>

          {/* Nueva Hora Entrada */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hora de entrada corregida *
            </label>
            <input
              type="time"
              value={nuevaHoraEntrada}
              onChange={(e) => setNuevaHoraEntrada(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Nueva Hora Salida */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Hora de salida corregida
            </label>
            <input
              type="time"
              value={nuevaHoraSalida}
              onChange={(e) => setNuevaHoraSalida(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 bg-orange-50 rounded-md border border-orange-200">
            <FiAlertCircle className="text-orange-600 mt-0.5 flex-shrink-0" size={16} />
            <p className="text-sm text-orange-800">
              La propuesta será enviada al empleado para su aprobación. El fichaje solo se modificará cuando el empleado la acepte.
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-ripple flex-1 flex items-center justify-center gap-2"
              disabled={loading}
            >
              <FiSend size={16} />
              {loading ? 'Enviando...' : 'Enviar Propuesta'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PropuestaCorreccionModal
